/**
 * X ADS › NEW CAMPAIGN — /x-ads/campaigns/new
 *
 *   1 Campaign    name, funding instrument, budgets            → POST campaigns (paused)
 *   2 Line item   objective, product, placements, bidding, goal, schedule   (form only)
 *   3 Targeting   …                                            → POST line-items with targeting (paused)
 *                                                                (later edits: PUT line-items/{id}/targeting)
 *   4 Creative    pick posts or create one                     → POST line-items/{id}/promoted-posts
 *   5 Review
 *
 * Whatever already exists on X is listed on the right and never created twice: a failed step
 * is retried on its own.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, CircleDashed, ExternalLink, Plus, RefreshCw, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { DateTimePicker } from '@/components/DateTimePicker';
import { PostComposer } from '@/components/x-ads/PostComposer';
import { TargetingForm } from '@/components/x-ads/TargetingForm';
import { XCheck } from '@/components/x-ads/XCheck';
import { XSelect } from '@/components/x-ads/XSelect';
import { XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import { biddingProblem, emptyTargeting, strategyAllowed, targetingCount, toTargetingInput, type TargetingState } from '@/lib/x-ads/lineItemRules';
import { useXConnection } from '@/lib/x-ads/connection';
import { dateTime, fundingName, label, localDayStart, localInputToIso, microsMoney, toMicros } from '@/lib/x-ads/format';
import { XA_HOME, XA_NEW } from '@/lib/x-ads/routes';
import { errMsg, xAdsService, type XCampaign, type XConstants, type XFundingInstrument, type XLineItem, type XPost, type XPromotedPost } from '@/services/x-ads.service';

const STEPS = ['Campaign', 'Line item', 'Targeting', 'Creative', 'Review'] as const;

interface LineItemForm {
  name: string; objective: string; productType: string; placements: string[]; bidStrategy: string; bid: string;
  goal: string; advertiserDomain: string; start: string; end: string;
}

export default function XAdsCampaignWizard() {
  const navigate = useNavigate();
  const { uid, account } = useXConnection();
  const currency = account?.currency ?? '';
  const [step, setStep] = useState(0);
  const [constants, setConstants] = useState<XConstants | null>(null);
  const [funding, setFunding] = useState<XFundingInstrument[] | null>(null);

  // Step 1
  const [campaignName, setCampaignName] = useState('');
  const [fundingId, setFundingId] = useState('');
  const [daily, setDaily] = useState('');
  const [total, setTotal] = useState('');
  // Step 2
  const [li, setLi] = useState<LineItemForm>({
    name: '', objective: 'ENGAGEMENTS', productType: 'PROMOTED_TWEETS', placements: ['ALL_ON_TWITTER'], bidStrategy: 'AUTO', bid: '',
    goal: '', advertiserDomain: '', start: '', end: '',
  });
  // Step 3
  const [targeting, setTargeting] = useState<TargetingState>(emptyTargeting);
  // Step 4
  const [posts, setPosts] = useState<XPost[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<'pick' | 'create'>('pick');

  // What exists on X.
  const [campaign, setCampaign] = useState<XCampaign | null>(null);
  const [lineItem, setLineItem] = useState<XLineItem | null>(null);
  const [savedTargeting, setSavedTargeting] = useState<string | null>(null);
  const [targetingError, setTargetingError] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<XPromotedPost[]>([]);
  const [busy, setBusy] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  useEffect(() => { xAdsService.constants().then(setConstants).catch(() => undefined); }, []);
  useEffect(() => {
    if (!uid) return;
    setFundingId(account?.fundingInstrumentId ?? '');
    xAdsService.fundingInstruments(uid).then((list) => {
      setFunding(list);
      // Keep the account's instrument when it is listed; otherwise the first one that can pay.
      setFundingId((cur) => (list.some((f) => f.id === cur) ? cur : list.find((f) => f.ableToFund)?.id ?? list[0]?.id ?? ''));
    }).catch(() => setFunding([]));
  }, [uid, account?.fundingInstrumentId]);
  useEffect(() => {
    if (step !== 3 || posts || !uid) return;
    xAdsService.posts(uid, 'PUBLISHED', 'NULLCAST').then(setPosts).catch(() => setPosts([]));
  }, [step, posts, uid]);

  const targetingBody = useMemo(() => toTargetingInput(targeting), [targeting]);
  const targetingSig = JSON.stringify(targetingBody);
  const bidMicros = toMicros(li.bid);
  const setLiField = <K extends keyof LineItemForm>(k: K, v: LineItemForm[K]) => setLi((s) => ({ ...s, [k]: v }));

  // ── Validation ──────────────────────────────────────────────────────────
  const campaignProblem = (): string | null => {
    if (!campaignName.trim()) return 'Give the campaign a name.';
    const d = toMicros(daily);
    if (!d || d < 1) return 'Enter a daily budget.';
    const t = toMicros(total);
    if (total && (!t || t < d)) return 'The total budget must be at least the daily budget.';
    if (funding && funding.length === 0 && !fundingId) return 'This ads account has no funding instrument. Add a payment method on ads.x.com.';
    return null;
  };
  const lineItemProblem = (): string | null => {
    if (!li.name.trim()) return 'Give the line item a name.';
    if (!li.objective) return 'Choose an objective.';
    if (li.placements.length === 0) return 'Choose at least one placement.';
    const bp = biddingProblem(li.objective, li.bidStrategy, bidMicros);
    if (bp) return bp;
    if (li.placements.includes('PUBLISHER_NETWORK') && !li.advertiserDomain.trim()) return 'The publisher network placement needs your website domain.';
    const start = li.start ? Date.parse(li.start) : Date.now();
    if (li.end && Date.parse(li.end) <= start) return 'The end time must be after the start time.';
    return null;
  };

  // ── Actions ─────────────────────────────────────────────────────────────
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setStepError(null);
    try { await fn(); }
    catch (e) { setStepError(errMsg(e)); }
    finally { setBusy(false); }
  };

  const nextFromCampaign = () => {
    const p = campaignProblem();
    if (p) { setStepError(p); return; }
    if (campaign) { setStep(1); return; }
    run(async () => {
      const created = await xAdsService.createCampaign(uid, {
        name: campaignName.trim(), dailyBudgetMicros: toMicros(daily)!, totalBudgetMicros: toMicros(total) ?? undefined, fundingInstrumentId: fundingId || undefined, status: 'PAUSED',
      });
      setCampaign(created);
      if (!li.name) setLiField('name', `${campaignName.trim()} — line item`);
      toast.success('Campaign created on X (paused)');
      setStep(1);
    });
  };

  const nextFromLineItem = () => {
    const p = lineItemProblem();
    if (p) { setStepError(p); return; }
    setStepError(null);
    setStep(2);
  };

  const saveTargeting = () => {
    if (!campaign) { setStep(0); return; }
    run(async () => {
      if (!lineItem) {
        const created = await xAdsService.createLineItem(uid, {
          campaignId: campaign.id, name: li.name.trim(), objective: li.objective, productType: li.productType, placements: li.placements,
          bidStrategy: li.bidStrategy, bidAmountMicros: li.bidStrategy === 'AUTO' ? undefined : bidMicros ?? undefined, goal: li.goal || undefined,
          advertiserDomain: li.placements.includes('PUBLISHER_NETWORK') ? li.advertiserDomain.trim() : undefined,
          startTime: localInputToIso(li.start), endTime: localInputToIso(li.end), status: 'PAUSED',
          targeting: targetingCount(targetingBody) ? targetingBody : undefined,
        });
        setLineItem(created);
        toast.success('Line item created on X (paused)');
        if (created.targetingError) {
          setTargetingError(created.targetingError);
          throw new Error(`The line item was created, but its targeting was not saved: ${created.targetingError}`);
        }
        setSavedTargeting(targetingSig);
        setTargetingError(null);
      } else if (savedTargeting !== targetingSig) {
        await xAdsService.setTargeting(uid, lineItem.id, targetingBody);
        setSavedTargeting(targetingSig);
        setTargetingError(null);
        toast.success('Targeting saved');
      }
      setStep(3);
    });
  };

  const promote = () => {
    if (!lineItem) { setStep(2); return; }
    const already = new Set(promoted.map((p) => p.tweetId));
    const ids = picked.filter((id) => !already.has(id));
    if (ids.length === 0) { setStepError(promoted.length ? null : 'Pick or create at least one post.'); if (promoted.length) setStep(4); return; }
    run(async () => {
      const rows = await xAdsService.promote(uid, lineItem.id, ids);
      setPromoted((p) => [...p, ...rows]);
      toast.success(`${rows.length} post${rows.length === 1 ? '' : 's'} added to the line item`);
      setStep(4);
    });
  };

  const onPostCreated = (post: XPost) => {
    if (!post.id) return;
    setPosts((list) => [post, ...(list ?? [])]);
    setPicked((p) => (p.includes(post.id!) ? p : [...p, post.id!]));
    setMode('pick');
  };

  const back = () => { setStepError(null); setStep((s) => Math.max(0, s - 1)); };
  const lineItemLocked = !!lineItem;
  const savedCount = savedTargeting ? targetingCount(JSON.parse(savedTargeting)) : 0;
  const objectives = constants?.objectives ?? ['ENGAGEMENTS'];
  const todayLocal = localDayStart();
  const pendingPicks = picked.filter((id) => !promoted.some((p) => p.tweetId === id)).length;

  return <XAdsShell section="New campaign" description="Campaign, line item, targeting and a promoted post — everything is created paused.">
    <ol className="xa-steps" aria-label="Wizard steps">
      {STEPS.map((s, i) => <li key={s} className={`xa-step ${i < step ? 'is-done' : ''}`} aria-current={i === step ? 'step' : undefined}>
        {i < step ? <Check size={13} /> : <span className="text-[11px]">{i + 1}</span>}{s}
      </li>)}
    </ol>
    {account?.sandbox && <XAdsNotice title="Sandbox account">Everything you create here stays in X's sandbox: it never serves and has no results.</XAdsNotice>}

    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <Card>
        {step === 0 && <section aria-label="Campaign">
          <h3 className="xa-panel-title mb-4">Campaign</h3>
          {campaign && <XAdsNotice title="Already created on X">This campaign exists (ID {campaign.id}). Change it later from the campaign list.</XAdsNotice>}
          <fieldset disabled={!!campaign} className="grid gap-x-6 md:grid-cols-2">
            <div className="md:col-span-2"><FormField label="Campaign name" htmlFor="xw-name" required><Input id="xw-name" maxLength={255} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} /></FormField></div>
            <FormField label="Funding instrument" htmlFor="xw-fi" hint="How X charges this campaign">
              <XSelect id="xw-fi" value={funding ? fundingId : ''} onChange={setFundingId} disabled={!funding?.length}
                placeholder={!funding ? 'Loading…' : 'None on this ads account'}
                options={(funding ?? []).map((f) => ({ value: f.id, label: `${fundingName(f)}${f.currency ? ` · ${f.currency}` : ''}${f.ableToFund ? '' : ' · cannot fund'}` }))} />
            </FormField>
            <div />
            <FormField label={`Daily budget${currency ? ` (${currency})` : ''}`} htmlFor="xw-daily" required>
              <Input id="xw-daily" type="number" min="0" step="0.01" inputMode="decimal" value={daily} onChange={(e) => setDaily(e.target.value)} />
            </FormField>
            <FormField label={`Total budget${currency ? ` (${currency})` : ''}`} htmlFor="xw-total" hint="Optional">
              <Input id="xw-total" type="number" min="0" step="0.01" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
            </FormField>
          </fieldset>
        </section>}

        {step === 1 && <section aria-label="Line item">
          <h3 className="xa-panel-title mb-4">Line item</h3>
          {lineItemLocked && <XAdsNotice title="Already created on X">This line item exists (ID {lineItem!.id}); its settings can no longer change here.</XAdsNotice>}
          <fieldset disabled={lineItemLocked} className="grid gap-x-6 md:grid-cols-2">
            <div className="md:col-span-2"><FormField label="Line item name" htmlFor="xw-li-name" required><Input id="xw-li-name" maxLength={255} value={li.name} onChange={(e) => setLiField('name', e.target.value)} /></FormField></div>
            <FormField label="Objective" htmlFor="xw-obj" required>
              <XSelect id="xw-obj" value={li.objective} onChange={(objective) => {
                if (!objective) return;
                setLi((s) => ({ ...s, objective, bidStrategy: strategyAllowed(objective, s.bidStrategy) ? s.bidStrategy : 'AUTO' }));
              }} options={objectives.map((o) => ({ value: o, label: label(o) }))} />
            </FormField>
            <FormField label="Product type" htmlFor="xw-prod">
              <XSelect id="xw-prod" value={li.productType} onChange={(v) => v && setLiField('productType', v)}
                options={(constants?.productTypes ?? ['PROMOTED_TWEETS']).map((p) => ({ value: p, label: label(p) }))} />
            </FormField>
            <FormField label="Bid strategy" htmlFor="xw-bid" hint="Greyed = not for this objective">
              <XSelect id="xw-bid" value={li.bidStrategy} onChange={(v) => v && setLiField('bidStrategy', v)}
                options={(constants?.bidStrategies ?? ['AUTO']).map((b) => ({
                  value: b, label: b === 'AUTO' ? 'Automatic' : b === 'MAX' ? 'Maximum bid' : b === 'TARGET' ? 'Target bid' : label(b), disabled: !strategyAllowed(li.objective, b),
                }))} />
            </FormField>
            <FormField label={`Bid amount${currency ? ` (${currency})` : ''}`} htmlFor="xw-bid-amt" hint={li.bidStrategy === 'AUTO' ? 'X sets the bid' : 'Required'}>
              <Input id="xw-bid-amt" type="number" min="0" step="0.01" disabled={li.bidStrategy === 'AUTO'} value={li.bid} onChange={(e) => setLiField('bid', e.target.value)} />
            </FormField>
            <FormField label="Goal" htmlFor="xw-goal" hint="Optional — what X optimises for">
              <XSelect id="xw-goal" value={li.goal} onChange={(v) => setLiField('goal', v)} clearable
                options={[{ value: '', label: "X's default for the objective" }, ...(constants?.goals ?? []).map((g) => ({ value: g, label: label(g) }))]} />
            </FormField>
            <div />
            <FormField label="Start" htmlFor="xw-start" hint="Your local time · empty = when enabled">
              <DateTimePicker value={li.start} minDate={todayLocal} onChange={(v) => setLiField('start', v)} placeholder="As soon as it is enabled" />
            </FormField>
            <FormField label="End" htmlFor="xw-end" hint="Optional">
              <DateTimePicker value={li.end} minDate={li.start ? new Date(li.start) : todayLocal} onChange={(v) => setLiField('end', v)} placeholder="No end date" />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Placements" hint="Where the ads appear">
                <div className="flex flex-wrap gap-2">
                  {(constants?.placements ?? ['ALL_ON_TWITTER']).map((p) => <label key={p} className={`xa-option !py-1.5 ${li.placements.includes(p) ? 'is-on' : ''}`}>
                    <XCheck className="mt-0.5" checked={li.placements.includes(p)} onChange={(on) => setLiField('placements', on ? [...li.placements, p] : li.placements.filter((x) => x !== p))} />
                    {p === 'ALL_ON_TWITTER' ? 'All on X' : label(p.replace(/^TWITTER_/, 'X_'))}
                  </label>)}
                </div>
              </FormField>
            </div>
            {li.placements.includes('PUBLISHER_NETWORK') && <FormField label="Advertiser domain" htmlFor="xw-domain" required hint="Needed for the publisher network">
              <Input id="xw-domain" placeholder="example.com" value={li.advertiserDomain} onChange={(e) => setLiField('advertiserDomain', e.target.value)} />
            </FormField>}
          </fieldset>
        </section>}

        {step === 2 && <section aria-label="Targeting">
          <h3 className="xa-panel-title mb-4">Targeting</h3>
          {targetingError && <XAdsNotice title="Targeting was not saved" error>{targetingError} Adjust it and press "Save targeting" to try again, or continue without it.</XAdsNotice>}
          <TargetingForm uid={uid} constants={constants} value={targeting} onChange={setTargeting} />
        </section>}

        {step === 3 && <section aria-label="Creative">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="xa-panel-title">Creative</h3>
            <div className="xa-subtabs !mb-0" role="group" aria-label="Creative source">
              <button type="button" aria-pressed={mode === 'pick'} onClick={() => setMode('pick')}>Pick existing posts</button>
              <button type="button" aria-pressed={mode === 'create'} onClick={() => setMode('create')}>Create a post</button>
            </div>
          </div>
          {mode === 'create' && <PostComposer uid={uid} onCreated={onPostCreated} />}
          {mode === 'pick' && <>
            {!posts && <div className="skeleton h-40 rounded-xl" aria-label="Loading posts" />}
            {posts?.length === 0 && <p className="xa-muted">No promoted-only posts yet. <button type="button" className="underline" onClick={() => setMode('create')}>Create one</button>.</p>}
            {!!posts?.length && <div className="max-h-[55vh] space-y-2 overflow-y-auto">
              {posts.filter((p) => p.id).map((p) => {
                const on = picked.includes(p.id!);
                const done = promoted.some((x) => x.tweetId === p.id);
                return <label key={p.id} className={`xa-option ${on ? 'is-on' : ''}`}>
                  <XCheck className="mt-0.5" checked={on || done} disabled={done} onChange={(c) => setPicked((s) => (c ? [...s, p.id!] : s.filter((x) => x !== p.id)))} />
                  {p.mediaUrls?.[0] && <img src={p.mediaUrls[0]} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" loading="lazy" />}
                  <span className="min-w-0">
                    <span className="line-clamp-2 block">{p.text || p.name || `Post ${p.id}`}</span>
                    <span className="xa-muted block">{p.cardUri ? 'Card · ' : ''}{dateTime(p.createdAt)} · {p.id}{done ? ' · added' : ''}</span>
                  </span>
                </label>;
              })}
            </div>}
          </>}
        </section>}

        {step === 4 && <section aria-label="Review">
          <h3 className="xa-panel-title mb-1">All set — everything is paused</h3>
          <p className="xa-muted mb-4">Nothing spends until you resume the campaign and the line item from the campaign list.</p>
          <dl className="grid gap-3 sm:grid-cols-2">
            {[
              ['Campaign', campaign ? `${campaign.name ?? campaignName} · ID ${campaign.id}` : '—'],
              ['Budget', `${microsMoney(toMicros(daily), currency)} daily${total ? ` · ${microsMoney(toMicros(total), currency)} total` : ''}`],
              ['Line item', lineItem ? `${lineItem.name ?? li.name} · ID ${lineItem.id}` : 'Not created'],
              ['Objective & bid', `${label(li.objective)} · ${li.bidStrategy === 'AUTO' ? 'Automatic bid' : `${label(li.bidStrategy)} ${microsMoney(bidMicros, currency)}`}`],
              ['Schedule', `${li.start ? dateTime(localInputToIso(li.start)) : 'When enabled'} → ${li.end ? dateTime(localInputToIso(li.end)) : 'no end'}`],
              ['Targeting', savedCount ? `${savedCount} criteria saved` : 'Everyone X allows'],
              ['Promoted posts', promoted.length ? promoted.map((p) => p.tweetId).join(', ') : 'None — add one before enabling'],
            ].map(([k, v]) => <div key={k} className="rounded-lg bg-surface-alt/60 p-3"><dt className="text-[11px] text-text-muted">{k}</dt><dd className="mt-1 break-words text-sm font-medium">{v}</dd></div>)}
          </dl>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to={XA_HOME}><Button iconRight={<ArrowRight size={15} />}>Go to campaigns</Button></Link>
            <Button variant="secondary" onClick={() => { navigate(XA_NEW); window.location.reload(); }} iconLeft={<Plus size={15} />}>Create another</Button>
          </div>
        </section>}

        {stepError && <p role="alert" className="mt-4 flex items-start gap-1.5 text-sm text-danger"><XCircle size={15} className="mt-0.5 shrink-0" />{stepError}</p>}

        {step < 4 && <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={back} disabled={step === 0 || busy} iconLeft={<ArrowLeft size={15} />}>Back</Button>
          <div className="flex flex-wrap gap-2">
            {step === 2 && targetingError && lineItem && <Button variant="ghost" disabled={busy} onClick={() => { setStepError(null); setStep(3); }}>Continue without targeting</Button>}
            {step === 3 && <Button variant="ghost" disabled={busy} onClick={() => { setStepError(null); setStep(4); }}>Skip for now</Button>}
            {step === 0 && <Button loading={busy} onClick={nextFromCampaign} iconRight={<ArrowRight size={15} />}>{campaign ? 'Next' : stepError ? 'Try again' : 'Create campaign'}</Button>}
            {step === 1 && <Button onClick={nextFromLineItem} iconRight={<ArrowRight size={15} />}>Next</Button>}
            {step === 2 && <Button loading={busy} onClick={saveTargeting} iconLeft={stepError ? <RefreshCw size={14} /> : undefined} iconRight={<ArrowRight size={15} />}>
              {!lineItem ? 'Create line item' : savedTargeting !== targetingSig ? 'Save targeting' : 'Next'}
            </Button>}
            {step === 3 && <Button loading={busy} onClick={promote} disabled={mode === 'create'} iconRight={<ArrowRight size={15} />}>
              {pendingPicks ? `Promote ${pendingPicks} post(s)` : 'Next'}
            </Button>}
          </div>
        </div>}
      </Card>

      <aside className="space-y-4">
        <Card>
          <p className="xa-eyebrow">CREATED ON X</p>
          <ul className="mt-3 space-y-3 text-sm">
            {[
              { k: 'Campaign', done: !!campaign, failed: false, v: campaign ? `ID ${campaign.id}` : 'Not yet' },
              { k: 'Line item', done: !!lineItem, failed: false, v: lineItem ? `ID ${lineItem.id}` : 'Not yet' },
              { k: 'Targeting', done: !!savedTargeting && !targetingError, failed: !!targetingError, v: targetingError ? 'Not saved — retry on step 3' : savedCount ? `${savedCount} criteria` : lineItem ? 'Everyone' : 'Not yet' },
              { k: 'Promoted posts', done: promoted.length > 0, failed: false, v: promoted.length ? `${promoted.length} post${promoted.length === 1 ? '' : 's'}` : 'Not yet' },
            ].map((r) => <li key={r.k} className="flex items-start gap-2">
              {r.failed ? <XCircle size={16} className="mt-0.5 shrink-0 text-danger" /> : r.done ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" /> : <CircleDashed size={16} className="mt-0.5 shrink-0 text-text-muted" />}
              <span><span className="block font-medium">{r.k}</span><span className="xa-muted">{r.v}</span></span>
            </li>)}
          </ul>
          {campaign && <a href="https://ads.x.com/" target="_blank" rel="noreferrer" className="xa-external mt-3 !px-0">Open in X Ads <ExternalLink size={13} /></a>}
        </Card>
        <Card>
          <p className="xa-eyebrow">GOOD TO KNOW</p>
          <p className="xa-muted mt-2">All line items of one campaign must share the same objective and product type. Maximum bid is not available for Reach or Followers; Target bid only for Reach, Followers and Website clicks.</p>
        </Card>
      </aside>
    </div>
  </XAdsShell>;
}
